import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, FileText, Loader2, Mail } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import EmailDocDialog from './EmailDocDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leave Salary Preview Component
export const LeaveSalaryPreviewDialog = ({ open, onClose, employee, employees }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(employee?.id || '');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [leaveData, setLeaveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  useEffect(() => {
    if (employee?.id) {
      setSelectedEmployee(employee.id);
    }
  }, [employee]);

  useEffect(() => {
    if (open && selectedEmployee) {
      fetchLeaveData();
    }
  }, [open, selectedEmployee, year, month]);

  const fetchLeaveData = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      // Fetch leave balance for preview
      const response = await fetch(`${API_URL}/api/leaves/balance/${selectedEmployee}?year=${year}`);
      if (!response.ok) throw new Error('Failed to fetch leave data');
      const data = await response.json();
      setLeaveData(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch leave data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    if (!selectedEmployee) {
      toast({ title: 'Error', description: 'Please select an employee', variant: 'destructive' });
      return;
    }
    
    setDownloading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/downloads/leave-salary/${selectedEmployee}?year=${year}&month=${month}&format=${format}`
      );
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const emp = employees?.find(e => e.id === selectedEmployee);
      const empCode = emp?.employee_code || 'EMP';
      const monthName = months.find(m => m.value === month)?.label || month;
      
      link.download = `LeaveSalary_${empCode}_${monthName}_${year}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 5000);
      
      toast({ title: 'Download Complete', description: `Leave Salary ${format.toUpperCase()} downloaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download Leave Salary', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const currentEmployee = employees?.find(e => e.id === selectedEmployee);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            Leave Salary Preview
          </DialogTitle>
        </DialogHeader>
        
        {/* Selection Controls */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-3">
          {/* Employee Selection */}
          {employees && employees.length > 0 && (
            <div>
              <Label className="text-sm font-medium text-green-800">Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.employee_code} - {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Year & Month Selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-green-800">Year</Label>
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2023, 2024, 2025, 2026].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-green-800">Month</Label>
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Preview Content */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            <span className="ml-2">Loading...</span>
          </div>
        ) : leaveData && currentEmployee ? (
          <div className="space-y-4">
            {/* Employee Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Employee</p>
                <p className="font-semibold">{currentEmployee.name}</p>
                <p className="text-sm text-gray-600">{currentEmployee.employee_code}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Period</p>
                <p className="font-semibold">{months.find(m => m.value === month)?.label} {year}</p>
              </div>
            </div>

            {/* Leave Balance Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-gray-500">Annual Entitlement</p>
                <p className="text-lg font-semibold text-blue-700">{leaveData.annual_entitlement?.toFixed(1)} days</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-xs text-gray-500">Carry Forward</p>
                <p className="text-lg font-semibold text-amber-700">{leaveData.carry_forward_from_previous?.toFixed(1)} days</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-xs text-gray-500">Balance</p>
                <p className="text-lg font-semibold text-green-700">{leaveData.annual_balance?.toFixed(1)} days</p>
              </div>
            </div>

            {/* Leave Taken */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Leave Taken This Year</p>
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Annual:</span>
                  <span className="font-medium ml-1">{leaveData.annual_taken} days</span>
                </div>
                <div>
                  <span className="text-gray-500">Sick:</span>
                  <span className="font-medium ml-1">{leaveData.sick_taken} days</span>
                </div>
                <div>
                  <span className="text-gray-500">Unpaid:</span>
                  <span className="font-medium ml-1">{leaveData.unpaid_taken} days</span>
                </div>
                <div>
                  <span className="text-gray-500">Emergency:</span>
                  <span className="font-medium ml-1">{leaveData.emergency_taken} days</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Select an employee to preview leave salary
          </div>
        )}

        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => setEmailOpen(true)}
            disabled={loading || !selectedEmployee || !leaveData}
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            data-testid="leave-salary-email-staff-btn"
          >
            <Mail className="h-4 w-4 mr-2" /> Email to Staff
          </Button>
          <Button
            variant="outline"
            onClick={() => handleDownload('excel')}
            disabled={downloading || loading || !selectedEmployee}
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Excel
          </Button>
          <Button
            onClick={() => handleDownload('pdf')}
            disabled={downloading || loading || !selectedEmployee}
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
      <EmailDocDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        title={`Email Leave Salary — ${months.find(m => m.value === month)?.label} ${year}`}
        recipientName={currentEmployee?.name}
        attachmentLabel="leave salary statement"
        payload={{ doc_type: 'leave_salary', employee_id: selectedEmployee, year, month }}
      />
    </Dialog>
  );
};

export default LeaveSalaryPreviewDialog;
